import type {
  AdoptSignatureRequest,
  CodeSent,
  ErrorCode,
  LinkInfo,
  SaveValuesResult,
  SessionView,
  Values,
  VerifyResult,
} from "@gsignatures/core";

/** What the preview route returns: the PDF as the server assembled it, and its fingerprint. */
export interface PreviewResult {
  sha256: string;
  /** The PDF itself; null when there is nothing to show (the fake has no file). */
  blob: Blob | null;
}

/**
 * The signer API as the page uses it. `client.ts` speaks HTTP to the real server;
 * `fake.ts` answers the same calls in the browser with core's rules, for the demo and tests.
 */
export interface SignerApi {
  /** True for the in-browser fake (shows demo hints; a missing demo file is a placeholder, not an error). */
  readonly fake: boolean;
  link(): Promise<LinkInfo>;
  sendCode(): Promise<CodeSent>;
  verify(code: string): Promise<VerifyResult>;
  session(): Promise<SessionView>;
  consent(version: string): Promise<SessionView>;
  viewed(documentId: string, sha256: string): Promise<void>;
  /** URL of the original (the letter, or the blank form). Same-origin with the session cookie. */
  fileUrl(documentId: string): string;
  accept(documentId: string): Promise<SessionView>;
  chooseForm(documentId: string, usPerson: boolean): Promise<SessionView>;
  saveValues(documentId: string, values: Values): Promise<SaveValuesResult>;
  review(): Promise<SessionView>;
  adoptSignature(req: AdoptSignatureRequest): Promise<SessionView>;
  preview(documentId: string): Promise<PreviewResult>;
  certificationShown(documentId: string): Promise<void>;
  sign(documentId: string, previewSha256: string): Promise<SessionView>;
  /** URL of the sealed copy, after completion. */
  downloadUrl(documentId: string): string;
  /** Adds a picture (an ID proof): a JPEG or PNG data URL the page has already made small. */
  addPicture(documentId: string, dataUrl: string, name?: string): Promise<SessionView>;
  removePicture(documentId: string, fileId: string): Promise<SessionView>;
  /** One of the signer's own pictures, for its thumbnail (same-origin, with the session cookie). */
  pictureUrl(documentId: string, fileId: string): string;
  decline(reason?: string): Promise<LinkInfo>;
  paper(): Promise<LinkInfo>;
}

export type FailureCode = ErrorCode | "network";

/** Every failed call becomes one of these, so the page can react by code. */
export class ApiFailure extends Error {
  constructor(
    public readonly code: FailureCode,
    message: string,
    public readonly status = 0,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiFailure";
  }
}

export const isFailure = (e: unknown): e is ApiFailure => e instanceof ApiFailure;

/** Failures worth trying again on their own (an autosave keeps retrying these). */
export function isTransient(e: unknown): boolean {
  return isFailure(e) && (e.code === "network" || e.code === "server_error" || e.code === "rate_limited");
}
